import {
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Schema as SchemaIcon,
} from '@mui/icons-material';
import { Button, IconButton, Stack, Tooltip, Chip } from '@mui/material';
import {
  getGridBooleanOperators,
  getGridStringOperators,
  getGridSingleSelectOperators,
  GridColDef,
  GridFilterOperator,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import { permission } from '@nohara-dxdev/oidc-core';
import { AdvancedFilterModel, formatDateTime, PaginatedDataGrid } from '@nohara-dxdev/react-core';
import { OidcProtectedComponent, useScopeIds } from '@nohara-dxdev/react-oidc';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useSettingValues } from '@/features/settings';
import { ItemTemplateEntity, ItemTemplateEntityWithSchema } from '@/types/api';

import { useItemTemplateSchemas } from '../api';
import {
  itemTemplateSortableFields,
  type ItemTemplateSortableField,
} from '../providers/item-template-filter-values-provider';

import { DeleteItemTemplate } from './delete-item-template';

const readItemTemplatePermissions = [permission.itemTemplate.read];

// Default sort configuration
const DEFAULT_SORT_FIELD: ItemTemplateSortableField = 'createdAt';
const DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'desc';

const filterOperatorTranslationMap = {
  equals: 'common.filterOperators.equals',
  is: 'common.filterOperators.equals',
  doesNotEqual: 'common.filterOperators.notEquals',
  not: 'common.filterOperators.notEquals',
  contains: 'common.filterOperators.contains',
  doesNotContain: 'common.filterOperators.notContains',
  startsWith: 'common.filterOperators.startsWith',
  endsWith: 'common.filterOperators.endsWith',
  isAnyOf: 'common.filterOperators.isAnyOf',
  isEmpty: 'common.filterOperators.isEmpty',
  isNotEmpty: 'common.filterOperators.isNotEmpty',
};

export interface ItemTemplateListProps {
  getItemTemplatePath: (itemTemplateId: string) => string;
  getItemTemplateSchemaPath?: (itemTemplateId: string) => string;
  onCreateTemplate?: () => void;
}

export const ItemTemplateList = ({
  getItemTemplatePath,
  getItemTemplateSchemaPath,
  onCreateTemplate,
}: ItemTemplateListProps) => {
  const { orgId } = useScopeIds('org');
  const { t } = useTranslation();
  const { getSettingEntityValue, getSettingValue, settings } = useSettingValues();

  // Get available itemTypes and constructionTypes for filter options
  const itemTypes = useMemo(() => {
    return getSettingValue<{ id: string; value: string }[]>('itemTypes', []).map(
      ({ id, value }) => ({
        value: id,
        label: value,
      }),
    );
  }, [getSettingValue, settings]);

  const constructionTypes = useMemo(() => {
    return getSettingValue<{ id: string; value: string }[]>('constructionTypes', []).map(
      ({ id, value }) => ({
        value: id,
        label: value,
      }),
    );
  }, [getSettingValue, settings]);

  // Server-side sorting state
  const [sortField, setSortField] = useState<ItemTemplateSortableField | undefined>(
    DEFAULT_SORT_FIELD,
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(DEFAULT_SORT_ORDER);

  // Advanced filter state
  const [pendingAdvancedFilterModel, setPendingAdvancedFilterModel] = useState<AdvancedFilterModel>(
    {
      conditions: [],
    },
  );
  const [appliedAdvancedFilterModel, setAppliedAdvancedFilterModel] = useState<AdvancedFilterModel>(
    {
      conditions: [],
    },
  );

  // Combine advancedFilterModel with applied filter model
  const combinedAdvancedFilterModel = useMemo(() => {
    if (appliedAdvancedFilterModel.conditions.length > 0) {
      return appliedAdvancedFilterModel;
    }
    return { conditions: [] };
  }, [appliedAdvancedFilterModel]);

  const itemTemplatesQuery = useItemTemplateSchemas({
    advancedFilterModel:
      combinedAdvancedFilterModel.conditions.length > 0 ? combinedAdvancedFilterModel : undefined,
    sortField,
    sortOrder,
  });

  // Convert state to MUI DataGrid sortModel format
  const sortModel = useMemo<GridSortModel>(() => {
    if (!sortField) {
      return [];
    }
    return [{ field: sortField, sort: sortOrder }];
  }, [sortField, sortOrder]);

  // Handle sort model changes from DataGrid
  const handleSortModelChange = useCallback((model: GridSortModel) => {
    if (model.length === 0) {
      // Reset to default when sorting is cleared
      setSortField(undefined);
      setSortOrder(undefined);
      return;
    }

    const { field, sort } = model[0];

    // Only allow sorting on fields that exist in the backend
    if (itemTemplateSortableFields.includes(field as ItemTemplateSortableField)) {
      setSortField(field as ItemTemplateSortableField);
      setSortOrder(sort ?? 'asc');
    }
  }, []);

  // Memoized helper function to translate filter operators
  const translateFilterOperators = useCallback(
    (operators: GridFilterOperator[], allowedOperators?: string[]): GridFilterOperator[] => {
      return operators
        .filter((operator) => !allowedOperators || allowedOperators.includes(operator.value))
        .map((operator) => {
          const translationKey =
            filterOperatorTranslationMap[
              operator.value as keyof typeof filterOperatorTranslationMap
            ];
          const defaultLabel = operator.label ?? '';
          return {
            ...operator,
            label: translationKey
              ? t(translationKey, { defaultValue: defaultLabel })
              : defaultLabel,
          };
        });
    },
    [t],
  );

  const stringFilterOperators = useMemo(() => {
    const operators = getGridStringOperators().filter((operator) => {
      return [
        'equals',
        'doesNotEqual',
        'contains',
        'doesNotContain',
        'startsWith',
        'endsWith',
        'isAnyOf',
      ].includes(operator.value);
    });
    return translateFilterOperators(operators) as GridFilterOperator<
      ItemTemplateEntityWithSchema,
      string,
      unknown
    >[];
  }, [translateFilterOperators]);
  const booleanFilterOperators = useMemo(() => {
    const operators = getGridBooleanOperators().filter((operator) => {
      return ['is', 'not'].includes(operator.value);
    });
    return translateFilterOperators(operators) as GridFilterOperator<
      ItemTemplateEntityWithSchema,
      boolean,
      unknown
    >[];
  }, [translateFilterOperators]);

  const singleSelectFilterOperators = useMemo(() => {
    const operators = getGridSingleSelectOperators().filter((operator) =>
      ['is', 'not', 'isAnyOf'].includes(operator.value),
    );
    return translateFilterOperators(operators) as GridFilterOperator<
      ItemTemplateEntityWithSchema,
      string,
      unknown
    >[];
  }, [translateFilterOperators]);

  const columns = useMemo(
    (): GridColDef<ItemTemplateEntityWithSchema>[] => [
      {
        field: 'name',
        headerName: t('itemTemplates.list.columns.name'),
        flex: 1.5,
        sortable: true,
        filterable: true,
        filterOperators: stringFilterOperators,
        valueGetter: (_, row) => row.name || t('itemTemplates.list.columns.untitledTemplate'),
      },
      {
        field: 'templateKey',
        headerName: t('itemTemplates.list.columns.templateKey'),
        flex: 0.7,
        sortable: true,
        filterable: true,
        filterOperators: stringFilterOperators,
      },
      {
        field: 'itemTypeId',
        headerName: t('itemTemplates.list.columns.itemType'),
        flex: 1,
        sortable: false,
        filterable: true,
        type: 'singleSelect',
        valueOptions: itemTypes,
        filterOperators: singleSelectFilterOperators,
      },
      {
        field: 'constructionTypeIds',
        headerName: t('itemTemplates.list.columns.constructionTypes'),
        flex: 1.5,
        sortable: false,
        filterable: true,
        type: 'singleSelect',
        valueOptions: constructionTypes,
        filterOperators: singleSelectFilterOperators,
        renderCell: ({ row }) => {
          const chips = row.constructionTypeIds
            .map((id) => ({ id, name: getSettingEntityValue<string>(id) }))
            .filter((chip): chip is { id: string; name: string } => Boolean(chip.name));
          if (chips.length === 0) {
            return null;
          }
          const visible = chips.slice(0, 2);
          const overflow = chips.slice(2);
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {visible.map(({ id, name }) => (
                <Chip key={id} label={name} size="small" variant="outlined" />
              ))}
              {overflow.length > 0 && (
                <Tooltip title={overflow.map(({ name }) => name).join(', ')}>
                  <Chip label={`+${overflow.length}`} size="small" variant="outlined" />
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'isBoardItem',
        headerName: t('itemTemplates.list.columns.isBoardItem'),
        flex: 0.8,
        sortable: false,
        filterable: true,
        type: 'boolean',
        filterOperators: booleanFilterOperators,
        renderCell: ({ row }) => {
          return (
            <Chip
              label={row.isBoardItem ? t('common.yes') : t('common.no')}
              size="small"
              color={row.isBoardItem ? 'success' : 'default'}
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'canConnectToBoard',
        headerName: t('itemTemplates.list.columns.canConnectToBoard'),
        flex: 0.8,
        sortable: false,
        filterable: true,
        type: 'boolean',
        filterOperators: booleanFilterOperators,
        renderCell: (params) => {
          const canConnectToBoard = params.row.canConnectToBoard;
          return (
            <Chip
              label={canConnectToBoard ? t('common.yes') : t('common.no')}
              size="small"
              color={canConnectToBoard ? 'success' : 'default'}
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'createdAt',
        headerName: t('itemTemplates.list.columns.createdAt'),
        flex: 0.8,
        sortable: true,
        filterable: true,
        type: 'dateTime',
        valueFormatter: (value: number) => formatDateTime(value),
      },
      {
        field: 'updatedAt',
        headerName: t('itemTemplates.list.columns.updatedAt'),
        flex: 0.8,
        sortable: true,
        filterable: true,
        type: 'dateTime',
        valueFormatter: (value: number) => formatDateTime(value),
      },
      {
        field: 'actions',
        headerName: t('itemTemplates.list.columns.actions'),
        flex: 1.2,
        // Floor for the 3 row-action icons (view / schema / delete) so they
        // stay visible at narrow viewport widths (MT1-1358).
        minWidth: 160,
        align: 'right',
        headerAlign: 'right',
        filterable: false,
        sortable: false,
        renderCell: (params: GridRenderCellParams<ItemTemplateEntity>) => (
          <>
            <OidcProtectedComponent
              requireAll={readItemTemplatePermissions}
              subject={{ orgId, itemTemplateId: params.row.id }}
            >
              <Tooltip title={t('itemTemplates.view.tooltip')}>
                <IconButton
                  color="primary"
                  size="small"
                  component={Link}
                  to={getItemTemplatePath(params.row.id)}
                  viewTransition
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {getItemTemplateSchemaPath && (
                <Tooltip title={t('itemTemplates.schemaDetail.tooltip')}>
                  <IconButton
                    color="secondary"
                    size="small"
                    component={Link}
                    to={getItemTemplateSchemaPath(params.row.id)}
                    viewTransition
                  >
                    <SchemaIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </OidcProtectedComponent>
            <DeleteItemTemplate itemTemplateId={params.row.id} />
          </>
        ),
      },
    ],
    [
      t,
      getSettingEntityValue,
      getItemTemplatePath,
      getItemTemplateSchemaPath,
      orgId,
      stringFilterOperators,
      booleanFilterOperators,
      singleSelectFilterOperators,
      itemTypes,
      constructionTypes,
    ],
  );

  // Handle advanced filter model changes from filter panel (only updates pending filter, not applied)
  const handleAdvancedFilterModelChange = useCallback((model: AdvancedFilterModel) => {
    setPendingAdvancedFilterModel(model);
  }, []);

  // Apply the pending advanced filter model (called from filter panel)
  const handleAdvancedFilterApply = useCallback((model: AdvancedFilterModel) => {
    setAppliedAdvancedFilterModel(model);
    setPendingAdvancedFilterModel(model);
  }, []);

  // Clear all filters (called from filter panel)
  const handleAdvancedFilterClear = useCallback(() => {
    const emptyFilterModel: AdvancedFilterModel = { conditions: [] };
    setPendingAdvancedFilterModel(emptyFilterModel);
    setAppliedAdvancedFilterModel(emptyFilterModel);
  }, []);

  return (
    <Stack spacing={3}>
      {/* Action Buttons */}
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        {onCreateTemplate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateTemplate}>
            {t('itemTemplates.create.button')}
          </Button>
        )}
      </Stack>

      <PaginatedDataGrid
        paginatedQuery={itemTemplatesQuery}
        columns={columns}
        showTopPagination
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        sortingMode="server"
        enableAdvancedFilters={true}
        advancedFilterModel={pendingAdvancedFilterModel}
        onAdvancedFilterModelChange={handleAdvancedFilterModelChange}
        onAdvancedFilterApply={handleAdvancedFilterApply}
        onAdvancedFilterClear={handleAdvancedFilterClear}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50, 100]}
        sx={{
          '--DataGrid-overlayHeight': '300px',
          '& .MuiDataGrid-cell': {
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-row': {
            minHeight: '60px',
          },
          '.MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon': {
            opacity: 1,
            color: 'secondary.main',
          },
        }}
      />
    </Stack>
  );
};
